// Sentry is loaded lazily (dynamic import) so it never enters the initial client bundle.
// captureClientException is fire-and-forget: errors are captured asynchronously after
// @sentry/react loads, which is fine for error-reporting (not a user-facing flow).
let _sentryModulePromise: Promise<typeof import('@sentry/react')> | null = null;

function getSentryModule(): Promise<typeof import('@sentry/react')> {
  if (!_sentryModulePromise) {
    _sentryModulePromise = import('@sentry/react');
  }
  return _sentryModulePromise;
}

/**
 * Report a client-side error to Sentry without throwing. Safe to call when Sentry is
 * uninitialized (no-op). Sentry is loaded lazily — the capture happens asynchronously.
 */
export function captureClientException(
  error: unknown,
  captureContext?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  },
): void {
  if (typeof window === 'undefined') return;
  getSentryModule()
    .then((Sentry) => {
      try {
        const err =
          error instanceof Error
            ? error
            : new Error(typeof error === 'string' ? error : JSON.stringify(error));
        Sentry.captureException(err, {
          tags: captureContext?.tags,
          extra: captureContext?.extra,
        });
      } catch {
        /* never throw from reporting */
      }
    })
    .catch(() => {
      /* ignore failures loading Sentry */
    });
}

/**
 * Run a synchronous callback; swallow errors after sending them to Sentry (e.g. effects,
 * third-party listeners we control).
 */
export function runClientCallbackSafely(
  fn: () => void,
  captureContext?: { tags?: Record<string, string>; extra?: Record<string, unknown>; label?: string },
): void {
  try {
    fn();
  } catch (error) {
    captureClientException(error, {
      tags: { source: 'runClientCallbackSafely', ...captureContext?.tags },
      extra: { label: captureContext?.label, ...captureContext?.extra },
    });
  }
}
